import {
  Aspect,
  dataUrlByteSize,
  MagicResult,
  modelResolutions,
  ModelSelection,
  Resolution,
} from '../model';
import { AiGenerationPayload, generateAi } from '../aiApi';
import { describeAiError } from '../aiErrors';
import { MagicGenerateRequest, MagicProvider, MagicProviderError } from './types';

// Generation through the asset_lab backend proxy — POST /api/asset-lab/ai/generations/.
//
// The browser never holds a provider secret: the user registered their Gemini /
// OpenAI key once (POST /ai/api-keys/) and from then on the request carries only
// `api_key_id`. The backend looks the key up, derives the provider from it, and
// calls Gemini or OpenAI server-side — which is also why references can be
// passed as bare `asset_id`s (the backend reads the asset's bytes itself) and
// why the payload has no provider field.
//
// Request (see aiApi.AiGenerationPayload):
//   { model, prompt, api_key_id, references?, aspect_ratio? + resolution? | size? }
// Response:
//   { images: [{ data /* base64, no prefix */, mime_type }], usage }
//
// Every failure comes back through safeShortcuts and is turned into a sentence
// by describeAiError — including the raw provider text the backend forwards on
// 502 and the budget object on 429. Nothing here may swallow that message.

// OpenAI's image endpoint accepts only a fixed set of canvas sizes, not free
// aspect ratios, so the studio's Aspect is mapped onto the nearest supported
// one: square, landscape 3:2, portrait 2:3 — or 'auto' to let the model decide.
function openAiSize(aspect: Aspect): string {
  switch (aspect) {
    case '1:1':
      return '1024x1024';
    case '4:3':
    case '16:9':
      return '1536x1024';
    case '3:4':
    case '9:16':
      return '1024x1536';
    case 'Auto':
    default:
      return 'auto';
  }
}

// Gemini takes the aspect ratio verbatim; 'Auto' means "don't constrain it", so
// the field is simply omitted.
function geminiAspect(aspect: Aspect): string | undefined {
  return aspect === 'Auto' ? undefined : aspect;
}

// The output size (backend `resolution` → Gemini imageConfig.imageSize). Sent
// only when the model actually offers the chosen value — the same list the
// dropdown was built from, so a size left over from another model (e.g. '4K'
// after switching to 2.5 flash) is dropped here instead of earning a 400.
function geminiResolution(model: string, resolution?: Resolution): Resolution | undefined {
  return resolution && modelResolutions(model, 'gemini').includes(resolution)
    ? resolution
    : undefined;
}

export function createBackendProvider(selection: ModelSelection): MagicProvider {
  return {
    id: 'backend',
    // The model list already came from the selected key, so anything the modal
    // can hand us is routable by definition.
    supportsModel: () => true,
    async generate(req: MagicGenerateRequest): Promise<MagicResult> {
      const payload: AiGenerationPayload = {
        model: selection.model,
        prompt: req.prompt,
        api_key_id: selection.apiKeyId,
        references: req.references ?? [],
        // Output shaping is provider-specific: Gemini gets aspect_ratio (+
        // resolution), OpenAI gets a single canvas size. Sending the wrong one
        // for the provider is a validation error, so only one branch is set.
        // `quality` / `images_count` are left to the backend defaults
        // (quality=auto, n=1) — the studio shows a single result, so asking for
        // more would only burn budget.
        ...(selection.provider === 'gemini'
          ? {
              aspect_ratio: geminiAspect(req.aspect),
              resolution: geminiResolution(selection.model, req.resolution),
            }
          : { size: openAiSize(req.aspect) }),
      };

      const res = await generateAi(payload, req.signal);

      if (!res.success) {
        // A cancelled request is not an error the user should read about — let
        // the abort propagate so the modal's genId guard simply drops it.
        //
        // TODO: unreachable today — MagicStudioModal never creates an
        // AbortController for generation and calls provider.generate() without a
        // `signal`, so `req.signal` is always undefined. Kept for when the modal
        // wires up a Cancel action; wiring it up is out of scope here.
        if (req.signal?.aborted) {
          throw res.data;
        }
        throw new MagicProviderError(describeAiError(res.data, 'Generation failed. Try again.'));
      }

      const { images, usage } = res.data.response;
      const image = images?.[0];
      if (!image?.data) {
        throw new MagicProviderError('The service returned no image. Try rephrasing the prompt.');
      }

      const mimeType = image.mime_type || 'image/png';
      const imageUrl = `data:${mimeType};base64,${image.data}`;
      return {
        id: `gen_${Date.now()}`,
        // The backend reports no seed (and the provider APIs don't expose one).
        // It is purely a client-side label for the result caption, the history
        // card and the download filename — same as the direct Gemini path.
        seed: Math.floor(Math.random() * 99999),
        aspect: req.aspect,
        format: mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'JPG' : 'PNG',
        imageUrl,
        bytes: dataUrlByteSize(imageUrl) ?? undefined,
        usage: usage
          ? {
              promptTokens: usage.prompt_tokens ?? null,
              outputTokens: usage.output_tokens ?? null,
              totalTokens: usage.total_tokens ?? null,
              costUsd: usage.cost_usd ?? null,
            }
          : undefined,
      };
    },
  };
}
